import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    CircularProgress,
    Card,
    CardContent,
    useTheme,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LabelList
} from 'recharts';
import { 
    EmojiEvents as TrophyIcon, 
    Description as DocumentIcon,
    ExpandMore as ExpandMoreIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Link as LinkIcon
} from '@mui/icons-material';
import api from '../../services/api';

const Reports = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRanking, setUserRanking] = useState([]);
    const [analysisStatus, setAnalysisStatus] = useState([]);
    const [jiraStatus, setJiraStatus] = useState([]);
    const [actTypes, setActTypes] = useState([]);
    const [expandedUser, setExpandedUser] = useState(null);
    const [userAnalyses, setUserAnalyses] = useState({});

    // Cores para os gráficos
    const COLORS = {
        gold: '#FFD700',
        silver: '#C0C0C0',
        bronze: '#CD7F32',
        success: theme.palette.success.main,
        error: theme.palette.error.main,
        jiraAttached: theme.palette.primary.main,
        jiraNotAttached: theme.palette.grey[500],
        actTypes: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.info.main,
            theme.palette.warning.main,
            theme.palette.error.main,
            theme.palette.success.main,
            theme.palette.grey[500]
        ]
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/analyses/reports');
            
            // Processar dados do ranking de usuários
            const rankingData = response.data.user_ranking.map((user, index) => ({
                ...user,
                position: index + 1,
                avatar_url: user.avatar_url ? `${process.env.REACT_APP_API_URL}${user.avatar_url}` : null,
                color: index === 0 ? COLORS.gold : index === 1 ? COLORS.silver : index === 2 ? COLORS.bronze : theme.palette.grey[500]
            }));
            setUserRanking(rankingData);

            // Processar dados de status das análises
            setAnalysisStatus([
                { name: 'Positivas', value: response.data.analysis_status.positive, color: COLORS.success },
                { name: 'Negativas', value: response.data.analysis_status.negative, color: COLORS.error }
            ]);

            // Processar dados do status do JIRA
            setJiraStatus([
                { name: 'Anexadas', value: response.data.jira_status.attached, color: COLORS.jiraAttached },
                { name: 'Não Anexadas', value: response.data.jira_status.not_attached, color: COLORS.jiraNotAttached }
            ]);

            // Processar dados dos tipos de atos
            setActTypes(response.data.act_types.map((type, index) => ({
                ...type,
                color: COLORS.actTypes[index % COLORS.actTypes.length]
            })));

        } catch (error) {
            setError('Erro ao carregar os relatórios');
            console.error('Erro ao carregar relatórios:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserAnalyses = async (userId) => {
        try {
            const response = await api.get(`/analyses?user_id=${userId}`);
            setUserAnalyses(prev => ({
                ...prev,
                [userId]: response.data
            }));
        } catch (error) {
            console.error('Erro ao carregar análises do usuário:', error);
        }
    };

    const handleUserClick = async (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
        } else {
            setExpandedUser(userId);
            if (!userAnalyses[userId]) {
                await fetchUserAnalyses(userId);
            }
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper sx={{ p: 1 }}>
                    <Typography variant="body2">
                        {`${payload[0].payload.name}: ${payload[0].value}`}
                    </Typography>
                </Paper>
            );
        }
        return null;
    };

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, payload }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill={theme.palette.text.primary}
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
            >
                {`${payload.name} (${(percent * 100).toFixed(0)}%)`}
            </text>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Relatórios
            </Typography>

            <Grid container spacing={3}>
                {/* Coluna da Esquerda */}
                <Grid item xs={12} md={6}>
                    {/* Ranking de Usuários */}
                    <Card 
                        sx={{ 
                            mb: 3,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(45deg, #1a237e 30%, #283593 90%)'
                                : 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)'
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrophyIcon sx={{ color: COLORS.gold }} />
                                Ranking de Analistas
                            </Typography>
                            <List>
                                {userRanking.map((user, index) => (
                                    <React.Fragment key={user.id}>
                                        <ListItem 
                                            sx={{
                                                transform: 'scale(1)',
                                                transition: 'transform 0.2s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                },
                                                flexDirection: 'column',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleUserClick(user.id)}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar 
                                                        src={user.avatar_url}
                                                        sx={{ 
                                                            border: `2px solid ${user.color}`,
                                                            backgroundColor: theme.palette.background.paper
                                                        }}
                                                    >
                                                        {user.username[0].toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                            {index + 1}º {user.username}
                                                        </Typography>
                                                    }
                                                    secondary={`${user.analysis_count} análises`}
                                                />
                                                <IconButton
                                                    sx={{
                                                        transform: expandedUser === user.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.3s'
                                                    }}
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </Box>
                                            <Collapse in={expandedUser === user.id} timeout="auto" unmountOnExit>
                                                <Box sx={{ mt: 2, mb: 1 }}>
                                                    <TableContainer component={Paper}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Ficha</TableCell>
                                                                    <TableCell>Tipo de Ato</TableCell>
                                                                    <TableCell align="center">Status</TableCell>
                                                                    <TableCell align="center">JIRA</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {userAnalyses[user.id]?.map((analysis) => (
                                                                    <TableRow key={analysis.id}>
                                                                        <TableCell>{analysis.ficha_number}</TableCell>
                                                                        <TableCell>{analysis.act_type || 'Não especificado'}</TableCell>
                                                                        <TableCell align="center">
                                                                            <Chip
                                                                                icon={analysis.competent === true || (analysis.competent === null && analysis.is_success === true) ? <CheckIcon /> : <CloseIcon />}
                                                                                label={analysis.competent === true || (analysis.competent === null && analysis.is_success === true) ? "Positiva" : "Negativa"}
                                                                                color={analysis.competent === true || (analysis.competent === null && analysis.is_success === true) ? "success" : "error"}
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <Chip
                                                                                icon={<LinkIcon />}
                                                                                label={analysis.jira_attached ? "Anexado" : "Não anexado"}
                                                                                color={analysis.jira_attached ? "primary" : "default"}
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            </Collapse>
                                        </ListItem>
                                        {index < userRanking.length - 1 && <Divider variant="inset" component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </CardContent>
                    </Card>

                    {/* Tipos de Atos */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DocumentIcon />
                                Tipos de Atos
                            </Typography>
                            <Box sx={{ height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={actTypes}
                                        layout="vertical"
                                        margin={{ top: 5, right: 50, left: 180, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis 
                                            type="number"
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis 
                                            type="category" 
                                            dataKey="name" 
                                            width={170}
                                            axisLine={false}
                                            tickLine={false}
                                            style={{
                                                fontSize: '11px',
                                                fontFamily: theme.typography.fontFamily
                                            }}
                                        />
                                        <Tooltip 
                                            content={<CustomTooltip />}
                                            cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                                        />
                                        <Bar 
                                            dataKey="value" 
                                            name="Quantidade"
                                            animationDuration={1500}
                                        >
                                            {actTypes.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={COLORS.actTypes[index % COLORS.actTypes.length]} 
                                                />
                                            ))}
                                            <LabelList 
                                                dataKey="value" 
                                                position="right"
                                                style={{
                                                    fill: theme.palette.text.primary,
                                                    fontSize: '12px',
                                                    fontFamily: theme.typography.fontFamily
                                                }}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Coluna da Direita */}
                <Grid item xs={12} md={6}>
                    <Grid container spacing={3}>
                        {/* Gráfico de Análises Positivas/Negativas */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Status das Análises
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={analysisStatus}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    label={renderCustomizedLabel}
                                                    labelLine={false}
                                                    animationBegin={0}
                                                    animationDuration={1500}
                                                >
                                                    {analysisStatus.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend 
                                                    formatter={(value, entry) => (
                                                        <span style={{ color: theme.palette.text.primary }}>
                                                            {value}
                                                        </span>
                                                    )}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Gráfico de Status JIRA */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Status JIRA
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={jiraStatus}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    label={renderCustomizedLabel}
                                                    labelLine={false}
                                                    animationBegin={0}
                                                    animationDuration={1500}
                                                >
                                                    {jiraStatus.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend 
                                                    formatter={(value, entry) => (
                                                        <span style={{ color: theme.palette.text.primary }}>
                                                            {value}
                                                        </span>
                                                    )}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Reports; 